import { OrderHistory, EmailProductData } from '../../stores/appOrderHistoryStore';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { CostPriceAnalysis } from '../../services/costPrice/costPriceService';
import { Subrecipe } from '../../services/subrecipes/subrecipesService';
import { InventoryCount } from '../../services/inventory/inventoryService';
import {
  groupCountsIntoSessions,
  SessionGroup,
} from '../../components/pages/inventory/inventoryOverviewUtils';
import { resolveProductIdentifier, getProductQuantity, selectValuationLine } from '../platformUtils';
import { getTableFormattedDate } from '../dateUtils';
import { baseProductKey } from '../inventoryProductKey';
import { pricePerStdUnit } from './shared';
import { buildNeededIndex } from './utilizationReport';

/**
 * Pure, React-free computation for the R7 True Consumption report — "the
 * wine-bottle report": over-ordering is fine if it's still on the shelf.
 *
 * The user picks a GROUP (default) or a single SECTION, plus two points in time
 * (two inventory counts / sessions). The window is dictated by the count
 * timestamps and IGNORES the page time filter. For every product we reconcile:
 *
 *   consumed = startCount + purchases-in-window − endCount
 *
 * and compare it against what the menu actually NEEDED in the same window
 * (R2's needed-index, recomputed with revenue filtered to the window):
 *
 *   true waste        = consumed − needed   (real loss)
 *   stock build-up    = purchased − consumed = endCount − startCount
 *
 * Valuation uses the END count's frozen €/std-unit, falling back to the latest
 * ordered price. All € on the ex-VAT basis, mirroring the rest of the reports.
 *
 * Inventory `productKey` is `${resolveProductIdentifier(...)}-${platform}` (identifier
 * first, `-` separator — distinct from the aggregation key's `${platform}::${identifier}`),
 * matching the server `buildLiveProductMap`. Orders are matched to counts on that same key
 * — same-SKU-from-two-suppliers conflation is accepted.
 */

// --- inventory-key matching (mirrors server buildLiveProductMap) ----------
//
// The inventory product key is `${resolveProductIdentifier(...)}-${platform}`: identifier =
// normalized name on `platformsWithoutArticleId` platforms + sentinel ids, `${id}::${alias}`
// for catch-all article ids, else the raw articleId (see platformUtils). The platform suffix is
// ESSENTIAL — articleIds are only unique within a supplier — and it's what's stored in
// `inventory_section_products.product_key` / `inventory_counts.items[].productKey`, so
// order/needed matching MUST reproduce the same composite key. Producer (buildLiveProductMap)
// and ALL consumers were migrated off the legacy raw rule in lockstep with a one-time DB key
// rewrite (spec-inventory-resolver-key-migration.md).

/** Inventory product key for an order product: `${resolveProductIdentifier(platform, articleId, name)}-${platform}`. */
export function inventoryKey(platform: string, articleId: string | undefined, name: string | undefined): string {
  const p = (platform || '').toLowerCase();
  const identifier = resolveProductIdentifier(p, articleId, name);
  if (!identifier) return '';
  return `${identifier}-${p}`;
}

// --- valuation ------------------------------------------------------------

/** Latest ordered €/std-unit for an inventory key across ALL order history
 *  (orders pre-sorted newest first). Fallback when the end count carries no price.
 *  `selectValuationLine` skips €0 lines and only falls back to a credit line for products
 *  that were never charged for (a retour crate is always negative — its magnitude is the value). */
export function latestPriceForKey(key: string, sortedAllOrders: OrderHistory[]): number | null {
  const candidates: EmailProductData[] = [];
  for (const o of sortedAllOrders) {
    for (const p of o.productData) {
      if (inventoryKey(o.platform, p.articleId, p.name) === key && pricePerStdUnit(p) !== null) {
        candidates.push(p);
      }
    }
  }
  const chosen = selectValuationLine(candidates, p => p.price);
  return chosen ? pricePerStdUnit(chosen) : null;
}

// --- window purchases -----------------------------------------------------

/** One inventory key's purchases over a window, kept in BOTH unit spaces. */
export interface WindowPurchase {
  /** Σ qty × stdAmount × stdMultiplier — base units, in `stdUnit`. */
  baseQty: number;
  /** Σ ordered units (crates/bottles as invoiced) — the space a 'unit' count lives in. */
  packageQty: number;
  /** The std unit `baseQty` is in; null when the window's lines disagree with each other. */
  stdUnit: string | null;
  name: string;
  hadUnmeasurable: boolean;
}

/**
 * Σ purchases per inventory key inside `[startAt, endAt]`, tracking the unit space each
 * line was measured in. `accept` narrows the universe (e.g. counted products only).
 */
export function collectWindowPurchases(
  orders: OrderHistory[],
  startAt: number,
  endAt: number,
  accept?: (key: string) => boolean
): Map<string, WindowPurchase> {
  const purchases = new Map<string, WindowPurchase>();
  for (const o of orders) {
    const t = new Date(o.orderDate).getTime();
    if (Number.isNaN(t) || t < startAt || t > endAt) continue;
    for (const p of o.productData) {
      const key = inventoryKey(o.platform, p.articleId, p.name);
      if (!key || (accept && !accept(key))) continue;
      const qty = getProductQuantity(p, o.platform);
      const stdAmount = p.quantityInfo?.stdAmount ?? 0;
      const stdMult = p.quantityInfo?.stdMultiplier ?? 0;
      const measurable = !!(stdAmount && stdMult);
      const lineUnit = measurable ? (p.quantityInfo?.stdUnit ?? null) : null;
      const existing = purchases.get(key);
      if (existing) {
        existing.baseQty += measurable ? qty * stdAmount * stdMult : 0;
        existing.packageQty += qty;
        // Lines that disagree on the unit poison the key — nothing can be valued safely.
        if (measurable && existing.stdUnit !== lineUnit) existing.stdUnit = null;
        if (!measurable) existing.hadUnmeasurable = true;
      } else {
        purchases.set(key, {
          baseQty: measurable ? qty * stdAmount * stdMult : 0,
          packageQty: qty,
          stdUnit: lineUnit,
          name: p.name,
          hadUnmeasurable: !measurable,
        });
      }
    }
  }
  return purchases;
}

/**
 * Purchases expressed in the unit space a COUNT was frozen in — the fix for valuing an
 * order line's base units with a count's €/std-unit when the two are different units.
 *
 * A count frozen as 'unit' has no base unit at all: it is denominated in whole ordered
 * packages and priced from `latestUnitPrice`, so it reconciles against `packageQty`.
 * Anything else must match the order line's own std unit exactly. When it cannot, the
 * caller gets `unitMismatch` and MUST treat the product as unmeasurable rather than
 * multiplying millilitres by a per-bottle price (which is how a week of drinks once
 * reconciled to €1.9M of purchases).
 */
export function purchasedQtyForCount(
  buy: WindowPurchase | undefined,
  countStdUnit: string
): { qty: number; unitMismatch: boolean } {
  if (!buy) return { qty: 0, unitMismatch: false };
  if (countStdUnit === 'unit') return { qty: buy.packageQty, unitMismatch: false };
  if (buy.stdUnit && buy.stdUnit === countStdUnit) return { qty: buy.baseQty, unitMismatch: false };
  // No measurable line at all is the pre-existing "unmeasurable" case, not a mismatch.
  if (buy.baseQty === 0 && !buy.stdUnit) return { qty: 0, unitMismatch: false };
  return { qty: 0, unitMismatch: true };
}

/** 'p' and 'unit' are the same physical space — 'unit' is what a line with no quantityInfo
 *  at all snapshots as, 'p' a line whose pack size was stated. Same space, different
 *  knowledge (see `isPieceSpace` in `server/utils/inventoryUnitHelper.ts`). */
function isPieceSpace(unit: string): boolean {
  return unit === 'p' || unit === 'unit';
}

/**
 * Theoretical usage expressed in the unit space a COUNT was frozen in — the needs-side
 * twin of `purchasedQtyForCount`, and the third guard on the same hazard.
 *
 * A cost-price ingredient's `quantityNeeded` is denominated in the ingredient row's OWN
 * `quantityInfo.stdUnit` snapshot, while a count is frozen in the inventory assignment's.
 * Both are snapshots of the same supplier line, and they drift apart whenever that line's
 * measured basis changes under a stable product key — a parser fix, an invoice reprocess,
 * or a factuur whose `quantityInfo` differs from the pakbon's. Subtracting one from the
 * other unconverted is how a 3 L bottle sold once read as "3000 pcs of theoretical usage"
 * on Leakage, wiping out the row's expected stock.
 *
 * Resolution mirrors `resolvePriceInAssignmentSpace`:
 *   - same space (or piece ↔ piece) → the quantity, unchanged. The overwhelmingly common path.
 *   - either side unknown ('') → unchanged, nothing to judge; a unit we never learned is
 *     not evidence of a contradiction.
 *   - measured need (g/ml) + piece-space count → divide by the pack size the SAME snapshot
 *     states: `stdAmount` per piece, `stdAmount × stdMultiplier` for a `'unit'` count
 *     (which is denominated in whole ordered packages, exactly as `purchasedQtyForCount`
 *     reads them). Exact, and reported as a mismatch because the setup is stale either way.
 *   - anything else (piece need vs measured count, g ↔ ml) → NO bridge exists, so no usage
 *     rather than an invented one; the caller must flag the row instead of reading 0 as
 *     "this product is in no recipe".
 */
export function neededQtyForCount(
  neededBase: number,
  need: { stdUnit: string; stdAmount?: number; stdMultiplier?: number } | undefined,
  countStdUnit: string
): { qty: number; unitMismatch: boolean; factor?: number } {
  if (!neededBase) return { qty: neededBase, unitMismatch: false };
  const needUnit = need?.stdUnit ?? '';
  if (!needUnit || !countStdUnit) return { qty: neededBase, unitMismatch: false };
  if (needUnit === countStdUnit || (isPieceSpace(needUnit) && isPieceSpace(countStdUnit))) {
    return { qty: neededBase, unitMismatch: false };
  }
  if (isPieceSpace(countStdUnit) && (needUnit === 'g' || needUnit === 'ml')) {
    const perPiece = need?.stdAmount ?? 0;
    const perCountUnit = countStdUnit === 'unit' ? perPiece * (need?.stdMultiplier ?? 0) : perPiece;
    if (perCountUnit > 0) return { qty: neededBase / perCountUnit, unitMismatch: true, factor: perCountUnit };
  }
  return { qty: 0, unitMismatch: true };
}

// --- frozen count spaces --------------------------------------------------

/**
 * The unit basis ONE inventory count entry was frozen in.
 *
 * A count item's `stdUnit` follows the inventory assignment as it stood AT COUNT TIME, and
 * a later assignment or unit fix never rewrites an already-frozen count
 * (`applyRelinkToCountItems` returns early on an unchanged key,
 * `server/domains/inventoryReview/relinkResolver.ts`). Two counts of the same product —
 * even two sections of the same session, which carry their own
 * `count_unit_to_std_factor` — therefore legitimately end up in different spaces.
 */
export interface FrozenCountSpace {
  /** Std unit the quantity is in ('g', 'ml', 'p', 'unit'; '' when unknown). */
  stdUnit: string;
  /** The physical unit staff actually counted in ('fles', 'kg', 'stuk'). */
  countUnit: string;
  /** Std units per ONE counted unit, backed out of the entry itself; null when unrecoverable. */
  stdPerCountUnit: number | null;
}

/** Read a frozen count item's own unit basis. A zero count states no factor (0/0), and a
 *  factor we cannot recover is null rather than a plausible-looking NaN/0. */
export function frozenCountSpace(item: {
  count: number;
  countUnit: string;
  stdUnit: string;
  countInStdUnit: number;
}): FrozenCountSpace {
  const factor = item.countInStdUnit / item.count;
  return {
    stdUnit: item.stdUnit,
    countUnit: item.countUnit,
    stdPerCountUnit: Number.isFinite(factor) && factor > 0 ? factor : null,
  };
}

/**
 * Factor that re-expresses a quantity frozen in `from`'s space as one in `to`'s space —
 * 1 when the two already agree, `null` when NO conversion exists.
 *
 * This is the fourth guard on the same hazard as `purchasedQtyForCount` and
 * `neededQtyForCount`, and the one that reaches the counts THEMSELVES. Nothing used to
 * check that the two counts a report subtracts were frozen in the same space: at
 * restaurant Jondani a 3 L wine bottle sits frozen as `count 1 / countUnit 'p' /
 * stdUnit 'p' / countInStdUnit 1`, while a sibling 3 L bottle in the same group froze as
 * `countUnit 'fles' / stdUnit 'ml' / countInStdUnit 3000`. Let one product freeze each way
 * across two sessions and its shelf reads as 2999 units of leakage — or of phantom stock,
 * depending which way round they fell.
 *
 * The only EXACT bridge is the one the frozen items carry themselves: when both were
 * counted in the same physical unit (both 'fles'), the ratio of their per-count-unit
 * factors converts one into the other, because the same bottles were counted and only the
 * basis they were recorded against changed. Anything else — a different count unit, a
 * factor neither item can produce, g ↔ ml — has no conversion at all, and the caller must
 * refuse to subtract rather than invent one.
 */
export function countSpaceBridge(from: FrozenCountSpace, to: FrozenCountSpace): number | null {
  // A unit we never learned is not evidence of a contradiction (same rule as `neededQtyForCount`).
  if (!from.stdUnit || !to.stdUnit) return 1;
  if (from.stdUnit === to.stdUnit) return 1;
  // 'p' and 'unit' are one space by convention — see `isPieceSpace`. Deliberately NOT
  // bridged through the factors: those differ by pack size, while these two units differ
  // only in how much the snapshot knew about the pack.
  if (isPieceSpace(from.stdUnit) && isPieceSpace(to.stdUnit)) return 1;
  if (!from.countUnit || from.countUnit !== to.countUnit) return null;
  if (!from.stdPerCountUnit || !to.stdPerCountUnit) return null;
  return to.stdPerCountUnit / from.stdPerCountUnit;
}

/** What folding one more frozen entry into a per-key aggregate does to that aggregate. */
export interface CountSpaceFold {
  /** Quantity to add, already in the aggregate's space. NULL = no bridge existed, so
   *  nothing was added and the aggregate's total no longer covers every entry. */
  qty: number | null;
  /** The entry was frozen in another space — converted, or refused. */
  mismatch: boolean;
  /** The aggregate must ADOPT this space: it held nothing yet, and a zero fits both. */
  retarget?: FrozenCountSpace;
}

/**
 * Fold one frozen count entry into a per-key aggregate already expressed in `agg.space`.
 *
 * ONE session can already disagree with itself — its sections are counted independently
 * and each carries its own assignment — yet every per-key aggregation in the reports
 * (`aggregateSession`, `sumCounts`, `sumLatestCounts`, the server's `itemMap`) used to add
 * straight through that, keeping the FIRST entry's unit and silently summing the rest into it.
 */
export function foldIntoCountSpace(
  agg: { totalQty: number; space: FrozenCountSpace },
  entry: { qty: number; space: FrozenCountSpace }
): CountSpaceFold {
  // Zero is the same quantity in every space, and "the other section counted none of it"
  // is by far the commonest way two entries differ — not a contradiction worth reporting.
  if (!entry.qty) return { qty: 0, mismatch: false };
  const bridge = countSpaceBridge(entry.space, agg.space);
  if (bridge !== null) return { qty: entry.qty * bridge, mismatch: bridge !== 1 };
  // Nothing bridged, but the aggregate is still empty — an empty shelf fits any space, so
  // adopt the entry's rather than declaring a contradiction against a zero.
  if (!agg.totalQty) return { qty: entry.qty, mismatch: false, retarget: entry.space };
  return { qty: null, mismatch: true };
}

/** How a whole session's frozen quantity for one product converts into another's space. */
export interface CountSpaceAlignment {
  /** Multiply every quantity of `from` by this to read it in `to`'s space. NULL = the two
   *  spaces have no bridge, so the counts must NOT be subtracted from each other at all. */
  factor: number | null;
  /** The two sessions froze this product in different spaces (converted, or refused). */
  mismatch: boolean;
}

/** The overwhelmingly common answer: both counts already live in the same space. */
export const COUNT_SPACES_ALIGNED: CountSpaceAlignment = { factor: 1, mismatch: false };

/**
 * Align a product's BASELINE count onto the space its END count was frozen in — the space
 * the row reports, and the space its €/std-unit is frozen per. See `countSpaceBridge` for
 * why the two can differ and what may bridge them.
 */
export function alignCountSpaces(
  from: { totalQty: number; space: FrozenCountSpace } | undefined,
  to: { space: FrozenCountSpace } | undefined
): CountSpaceAlignment {
  // Only one count holds this product: there is one space in play and nothing to reconcile.
  if (!from || !to) return COUNT_SPACES_ALIGNED;
  // Nothing on the shelf converts to nothing on the shelf, whatever the units say.
  if (!from.totalQty) return COUNT_SPACES_ALIGNED;
  const bridge = countSpaceBridge(from.space, to.space);
  if (bridge === null) return { factor: null, mismatch: true };
  return { factor: bridge, mismatch: bridge !== 1 };
}

// --- window selection -----------------------------------------------------

/** A selectable count / session point in time. */
export interface ConsumptionPoint {
  key: string; // session.key (group scope) or count.id (section scope)
  label: string; // formatted date
  completedAt: number; // ms
  totalValue: number;
}

/** A group the user can run the report on (only those with ≥2 sessions). */
export interface ConsumptionGroupOption {
  groupKey: string;
  groupName: string | null;
  points: ConsumptionPoint[]; // newest-first
}

/** A single section the user can run the report on (only those with ≥2 counts). */
export interface ConsumptionSectionOption {
  sectionId: string;
  sectionName: string;
  groupName: string | null;
  points: ConsumptionPoint[]; // newest-first
}

export interface ConsumptionWindows {
  groups: ConsumptionGroupOption[];
  sections: ConsumptionSectionOption[];
  /** True when at least one group or section has ≥2 counts to compare. */
  hasAnyWindow: boolean;
}

/** Stable bucket for "one physical group". Legacy/ungrouped counts collapse together. */
function groupKeyOf(session: SessionGroup): string {
  return session.groupId ?? session.groupName ?? 'ungrouped';
}

/**
 * Build the selectable windows (groups + sections with ≥2 counts). Counts of
 * deleted sections (`inventorySectionId === null`) are not selectable for the
 * section view; group sessions still surface them as part of the session total.
 */
export function buildConsumptionWindows(countsHistory: InventoryCount[]): ConsumptionWindows {
  const sessions = groupCountsIntoSessions(countsHistory); // newest-first

  // --- Groups: sessions bucketed by group ---
  const sessionsByGroup = new Map<string, SessionGroup[]>();
  const groupNameByKey = new Map<string, string | null>();
  sessions.forEach((s) => {
    const key = groupKeyOf(s);
    const list = sessionsByGroup.get(key) ?? [];
    list.push(s); // newest-first preserved
    sessionsByGroup.set(key, list);
    if (!groupNameByKey.has(key)) groupNameByKey.set(key, s.groupName ?? null);
  });
  const groups: ConsumptionGroupOption[] = Array.from(sessionsByGroup.entries())
    .filter(([, list]) => list.length >= 2)
    .map(([groupKey, list]) => ({
      groupKey,
      groupName: groupNameByKey.get(groupKey) ?? null,
      points: list.map((s) => ({
        key: s.key,
        label: getTableFormattedDate(s.completedAt),
        completedAt: s.completedAt.getTime(),
        totalValue: s.totalValue,
      })),
    }))
    .sort((a, b) => (a.groupName ?? '').localeCompare(b.groupName ?? ''));

  // --- Sections: individual counts bucketed by section (skip deleted sections) ---
  const countsBySection = new Map<string, InventoryCount[]>();
  countsHistory.forEach((c) => {
    if (!c.inventorySectionId) return; // deleted section — not selectable
    const list = countsBySection.get(c.inventorySectionId) ?? [];
    list.push(c);
    countsBySection.set(c.inventorySectionId, list);
  });
  const sections: ConsumptionSectionOption[] = Array.from(countsBySection.entries())
    .filter(([, list]) => list.length >= 2)
    .map(([sectionId, list]) => {
      const sorted = [...list].sort((a, b) => b.countedAt.getTime() - a.countedAt.getTime());
      return {
        sectionId,
        sectionName: sorted[0].sectionNameSnapshot,
        groupName: sorted[0].groupNameSnapshot,
        points: sorted.map((c) => ({
          key: c.id,
          label: getTableFormattedDate(c.countedAt),
          completedAt: c.countedAt.getTime(),
          totalValue: c.totalValue,
        })),
      };
    })
    .sort((a, b) => a.sectionName.localeCompare(b.sectionName));

  return { groups, sections, hasAnyWindow: groups.length > 0 || sections.length > 0 };
}

// --- report computation ---------------------------------------------------

/** A reconciled product row: start/end stock, purchases, consumption vs needed. */
export interface ConsumptionRow {
  key: string;
  productName: string;
  /** Cost-price dishes that use this product (for the "used in" column). */
  dishes: string[];
  stdUnit: string;
  /**
   * Std-unit quantities. Real numbers on every row EXCEPT one whose two counts were frozen
   * in unit spaces nothing could bridge (`countQtyUnreadable`), where subtracting them
   * would invent stock — those are null and must render "—", never 0.
   */
  startQty: number | null; // base/std units held at the start count
  endQty: number | null; // base/std units held at the end count
  /** Measurable purchases in the window (base units; unmeasurable lines add 0). */
  purchasedQty: number | null;
  consumedQty: number | null; // start + purchased − end
  neededQty: number | null; // menu-derived requirement over the window
  trueWasteQty: number | null; // consumed − needed
  stockBuildUpQty: number | null; // purchased − consumed = end − start
  /** €/std-unit used for valuation (end count, then latest order). Null = no price. */
  pricePerStdUnit: number | null;
  consumedEur: number | null;
  trueWasteEur: number | null;
  stockBuildUpEur: number | null;
  /** consumed < 0 → likely a count error or unmeasurable purchases. */
  negativeConsumption: boolean;
  /** Some purchases in the window had no quantityInfo → consumption understated. */
  hasUnmeasurablePurchases: boolean;
  /**
   * The two counts (or two sections of one count) froze this product in DIFFERENT std-unit
   * spaces — see `countSpaceBridge`. The quantities below were converted through the pack
   * size the frozen items themselves state, so they are exact, but the product's unit setup
   * is stale and the reader should know the number was rebased.
   *
   * OPTIONAL and set only when true, so an untouched report serialises exactly as before.
   */
  countUnitMismatch?: boolean;
  /** …and nothing could bridge them, so this product has no quantity in any single space:
   *  every quantity field above is null, and so is every € derived from one. The row is
   *  kept to name the product and its broken unit setup, not to report a number. */
  countQtyUnreadable?: boolean;
  /** Section scope only: the same product is also held in other sections. */
  crossSection: boolean;
}

/** A custom-ingredient row: count deltas only (never matches orders). */
export interface ConsumptionCustomRow {
  key: string;
  productName: string;
  stdUnit: string;
  /**
   * Real numbers, except on a row whose two counts were frozen in unit spaces nothing
   * could bridge (`countQtyUnreadable`) — there is no delta to take between them, so the
   * fields are null and must render "—". Custom ingredients are assigned to sections like
   * any other product, so the same drift reaches them.
   */
  startQty: number | null;
  endQty: number | null;
  deltaQty: number | null; // end − start
  deltaEur: number | null;
  /** OPTIONAL, set only when true — see `countSpaceBridge`. */
  countQtyUnreadable?: boolean;
  /**
   * Display basis for the count-unit view: `displayQty = stdQty / displayStdPerCountUnit`.
   * Set ONLY by `computeInventoryVariance` (leakage), and only when a basis resolved —
   * `computeConsumptionReport` (R7) leaves both undefined, so its output is unchanged.
   * See `VarianceRow.displayCountUnit` for why the ratio is never re-derived elsewhere.
   */
  displayCountUnit?: string;
  displayStdPerCountUnit?: number;
}

export interface ConsumptionWindowInfo {
  scope: 'group' | 'section';
  label: string; // group / section name
  startLabel: string;
  endLabel: string;
  startAt: number;
  endAt: number;
}

export interface ConsumptionReportData {
  hasData: boolean;
  window: ConsumptionWindowInfo | null;
  rows: ConsumptionRow[];
  customItems: ConsumptionCustomRow[];
  totalConsumedEur: number;
  totalTrueWasteEur: number;
  stockDeltaEur: number;
  /** Names of order products bought in-window with no quantityInfo (understated). */
  unmeasurableProductNames: string[];
  /** True for the section scope when at least one row is also held elsewhere. */
  hasCrossSection: boolean;
}

export interface ConsumptionReportInputs {
  countsHistory: InventoryCount[];
  /** All-time order history (window-filtered here; full list for latest price). */
  orders: OrderHistory[];
  /** All-time revenue history (window-filtered here for the needed-index). */
  revenue: RevenueHistory[];
  costPriceList: CostPriceAnalysis[];
  subrecipesById: Map<string, Subrecipe>;
  defaultWastePercentage: number;
  currentRestaurantId: string;
  scope: 'group' | 'section';
  /** groupKey (group scope) or sectionId (section scope). */
  selectionId: string;
  startPointKey: string;
  endPointKey: string;
}

const EMPTY: ConsumptionReportData = {
  hasData: false,
  window: null,
  rows: [],
  customItems: [],
  totalConsumedEur: 0,
  totalTrueWasteEur: 0,
  stockDeltaEur: 0,
  unmeasurableProductNames: [],
  hasCrossSection: false,
};

/** One point in time's stock for a product, plus the unit basis it was frozen in. */
interface CountTotal {
  qty: number;
  name: string;
  space: FrozenCountSpace;
  price: number | null;
  /** Some entry summed here was frozen in another space than `space` — see `foldIntoCountSpace`. */
  unitMismatch: boolean;
  /** …and could not be converted, so `qty` no longer covers every entry. */
  qtyUnreadable: boolean;
}

/** Sum a set of counts' items by inventory key (base std units + display info).
 *  Variant rows (`…__v<n>`) recombine with their base — purchases/needs sit on base keys.
 *  Entries frozen in a different unit space than the one already summed here are converted
 *  when the frozen items allow it, and otherwise refused rather than added blindly. */
function sumCounts(counts: InventoryCount[]): Map<string, CountTotal> {
  const map = new Map<string, CountTotal>();
  counts.forEach((c) => {
    c.items.forEach((item) => {
      const key = baseProductKey(item.productKey);
      const space = frozenCountSpace(item);
      const existing = map.get(key);
      if (existing) {
        const fold = foldIntoCountSpace(
          { totalQty: existing.qty, space: existing.space },
          { qty: item.countInStdUnit, space }
        );
        if (fold.retarget) existing.space = fold.retarget;
        if (fold.mismatch) existing.unitMismatch = true;
        if (fold.qty === null) existing.qtyUnreadable = true;
        else existing.qty += fold.qty;
        if (item.pricePerStdUnit) existing.price = item.pricePerStdUnit;
      } else {
        map.set(key, {
          qty: item.countInStdUnit,
          name: item.productName,
          space,
          price: item.pricePerStdUnit || null,
          unitMismatch: false,
          qtyUnreadable: false,
        });
      }
    });
  });
  return map;
}

export function computeConsumptionReport(inputs: ConsumptionReportInputs): ConsumptionReportData {
  const {
    countsHistory,
    orders,
    revenue,
    costPriceList,
    subrecipesById,
    defaultWastePercentage,
    currentRestaurantId,
    scope,
    selectionId,
    startPointKey,
    endPointKey,
  } = inputs;

  if (!selectionId || !startPointKey || !endPointKey || startPointKey === endPointKey) return EMPTY;

  // --- Resolve the two selected points to their constituent counts + timestamps ---
  let startCounts: InventoryCount[] = [];
  let endCounts: InventoryCount[] = [];
  let startAt = 0;
  let endAt = 0;
  let scopeLabel = '';
  let selectedSectionId: string | null = null;

  if (scope === 'group') {
    const sessions = groupCountsIntoSessions(countsHistory).filter((s) => groupKeyOf(s) === selectionId);
    const a = sessions.find((s) => s.key === startPointKey);
    const b = sessions.find((s) => s.key === endPointKey);
    if (!a || !b) return EMPTY;
    scopeLabel = a.groupName ?? b.groupName ?? '';
    const [older, newer] = a.completedAt.getTime() <= b.completedAt.getTime() ? [a, b] : [b, a];
    startCounts = older.counts;
    endCounts = newer.counts;
    startAt = older.completedAt.getTime();
    endAt = newer.completedAt.getTime();
  } else {
    const sectionCounts = countsHistory.filter((c) => c.inventorySectionId === selectionId);
    const a = sectionCounts.find((c) => c.id === startPointKey);
    const b = sectionCounts.find((c) => c.id === endPointKey);
    if (!a || !b) return EMPTY;
    selectedSectionId = selectionId;
    scopeLabel = a.sectionNameSnapshot || b.sectionNameSnapshot || '';
    const [older, newer] = a.countedAt.getTime() <= b.countedAt.getTime() ? [a, b] : [b, a];
    startCounts = [older];
    endCounts = [newer];
    startAt = older.countedAt.getTime();
    endAt = newer.countedAt.getTime();
  }

  const startTotals = sumCounts(startCounts);
  const endTotals = sumCounts(endCounts);

  // --- Purchases in the window (matched orders, base units) ---
  const purchases = collectWindowPurchases(orders, startAt, endAt);

  // --- Needed-from-menu over the window (R2 needed-index, re-keyed to inventory keys) ---
  const windowRevenue = revenue.filter((r) => {
    const t = new Date(r.date).getTime();
    return !Number.isNaN(t) && t >= startAt && t <= endAt;
  });
  // Key the needed index by the RAW inventory key directly (not the aggregation key),
  // so it lines up byte-for-byte with the count items + purchases above. The window
  // purchases double as the interchangeable-pool supply for alternatives (no-op without
  // any alternatives — the allocation engine short-circuits to today's math).
  const supply = new Map(Array.from(purchases.entries()).map(([k, v]) => [k, v.baseQty]));
  const sharedNeeded = buildNeededIndex(
    costPriceList,
    windowRevenue,
    subrecipesById,
    defaultWastePercentage,
    currentRestaurantId,
    inventoryKey,
    supply
  );
  const neededByKey = new Map<string, { qty: number; dishes: Set<string> }>();
  Array.from(sharedNeeded.entries()).forEach(([key, info]) => {
    neededByKey.set(key, { qty: info.base, dishes: new Set(info.dishes) });
  });

  // --- Cross-section presence (section scope only; base keys — variants count) ---
  const otherSectionKeys = new Set<string>();
  if (selectedSectionId) {
    countsHistory.forEach((c) => {
      if (c.inventorySectionId === selectedSectionId) return;
      c.items.forEach((item) => otherSectionKeys.add(baseProductKey(item.productKey)));
    });
  }

  // Latest-price fallback walks the full order history newest-first.
  const sortedAllOrders = [...orders].sort(
    (a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
  );

  // --- Build the product universe (counts ∪ purchases), split custom out ---
  const allKeys = new Set<string>([
    ...Array.from(startTotals.keys()),
    ...Array.from(endTotals.keys()),
    ...Array.from(purchases.keys()),
  ]);

  const rows: ConsumptionRow[] = [];
  const customItems: ConsumptionCustomRow[] = [];
  let totalConsumedEur = 0;
  let totalTrueWasteEur = 0;
  let stockDeltaEur = 0;
  const unmeasurableNames = new Set<string>();
  let hasCrossSection = false;

  Array.from(allKeys).forEach((key) => {
    const start = startTotals.get(key);
    const end = endTotals.get(key);
    const buy = purchases.get(key);
    // The two counts are INDEPENDENT freezes of the same product and can disagree about
    // the unit space it lives in — re-express the start count in the end count's space
    // (the one this row reports and prices in), or refuse to subtract them at all.
    const alignment = alignCountSpaces(
      start ? { totalQty: start.qty, space: start.space } : undefined,
      end ? { space: end.space } : undefined
    );
    const countQtyUnreadable = !!start?.qtyUnreadable || !!end?.qtyUnreadable || alignment.factor === null;
    const countUnitMismatch = !!start?.unitMismatch || !!end?.unitMismatch || alignment.mismatch;
    const startQty = (start?.qty ?? 0) * (alignment.factor ?? 1);
    const endQty = end?.qty ?? 0;
    // Purchase-only keys (never counted) have no frozen unit — they are already in the
    // order line's own space, so read purchases there rather than declaring a mismatch.
    const stdUnit = end?.space.stdUnit || start?.space.stdUnit || buy?.stdUnit || '';
    const name = end?.name || start?.name || buy?.name || '';

    // Custom items never match orders → count deltas only, no consumption. A counted
    // subrecipe (prep) is diverted for the same reason and one more: its ingredients are
    // charged to `needed` when a dish is SOLD, so reconciling the prep itself against
    // orders it can never match would read every batch as pure waste. Exploding a prep
    // into its ingredients is the Leakage tab's job, deliberately not R7's.
    if (key.startsWith('custom:') || key.startsWith('subrecipe:')) {
      const price = end?.price ?? start?.price ?? null;
      const deltaQty = endQty - startQty;
      customItems.push({
        key,
        productName: name,
        stdUnit,
        // Two counts in unbridgeable spaces have no delta between them — null, not 0.
        startQty: countQtyUnreadable ? null : startQty,
        endQty: countQtyUnreadable ? null : endQty,
        deltaQty: countQtyUnreadable ? null : deltaQty,
        deltaEur: price !== null && !countQtyUnreadable ? deltaQty * price : null,
        ...(countQtyUnreadable ? { countQtyUnreadable: true } : {}),
      });
      return;
    }

    const { qty: purchasedQty, unitMismatch } = purchasedQtyForCount(buy, stdUnit);
    const need = neededByKey.get(key);
    const neededQty = need?.qty ?? 0;
    const consumedQty = startQty + purchasedQty - endQty;
    const trueWasteQty = consumedQty - neededQty;
    const stockBuildUpQty = endQty - startQty;

    const price = end?.price ?? latestPriceForKey(key, sortedAllOrders);
    // A row with no single quantity space has no quantity to value either: R7 reports
    // consumption, waste and build-up ONLY as `quantity × price`, so there is nothing
    // honest left to print. The row survives to name the product and its stale units.
    const val = (q: number): number | null => (price !== null && !countQtyUnreadable ? q * price : null);
    const consumedEur = val(consumedQty);
    const trueWasteEur = val(trueWasteQty);
    const stockBuildUpEur = val(stockBuildUpQty);

    // A count/order unit mismatch is unmeasurable for the same reason: purchases in this
    // product's own space cannot be established, so consumption is understated, not wrong.
    const hasUnmeasurablePurchases = !!buy?.hadUnmeasurable || unitMismatch;
    if (hasUnmeasurablePurchases) unmeasurableNames.add(name);
    const crossSection = otherSectionKeys.has(key);
    if (crossSection) hasCrossSection = true;

    if (consumedEur !== null) totalConsumedEur += consumedEur;
    if (trueWasteEur !== null && trueWasteEur > 0) totalTrueWasteEur += trueWasteEur;
    if (stockBuildUpEur !== null) stockDeltaEur += stockBuildUpEur;

    rows.push({
      key,
      productName: name,
      dishes: need ? Array.from(need.dishes).sort() : [],
      stdUnit,
      // Null, never 0: "no quantity exists in any single space" and "the shelf is empty"
      // are opposite statements, and a 0 here reads as the second one.
      startQty: countQtyUnreadable ? null : startQty,
      endQty: countQtyUnreadable ? null : endQty,
      purchasedQty: countQtyUnreadable ? null : purchasedQty,
      consumedQty: countQtyUnreadable ? null : consumedQty,
      neededQty: countQtyUnreadable ? null : neededQty,
      trueWasteQty: countQtyUnreadable ? null : trueWasteQty,
      stockBuildUpQty: countQtyUnreadable ? null : stockBuildUpQty,
      pricePerStdUnit: price,
      consumedEur,
      trueWasteEur,
      stockBuildUpEur,
      negativeConsumption: !countQtyUnreadable && consumedQty < 0,
      hasUnmeasurablePurchases,
      ...(countUnitMismatch ? { countUnitMismatch: true } : {}),
      ...(countQtyUnreadable ? { countQtyUnreadable: true } : {}),
      crossSection,
    });
  });

  // True waste € desc, then consumed € desc — biggest losses first.
  rows.sort(
    (a, b) =>
      (b.trueWasteEur ?? -Infinity) - (a.trueWasteEur ?? -Infinity) ||
      (b.consumedEur ?? -Infinity) - (a.consumedEur ?? -Infinity)
  );
  customItems.sort((a, b) => Math.abs(b.deltaEur ?? 0) - Math.abs(a.deltaEur ?? 0));

  return {
    hasData: true,
    window: {
      scope,
      label: scopeLabel,
      startLabel: getTableFormattedDate(new Date(startAt)),
      endLabel: getTableFormattedDate(new Date(endAt)),
      startAt,
      endAt,
    },
    rows,
    customItems,
    totalConsumedEur,
    totalTrueWasteEur,
    stockDeltaEur,
    unmeasurableProductNames: Array.from(unmeasurableNames).sort(),
    hasCrossSection,
  };
}
